import { MessageSquarePlus, Type, EllipsisVertical, Scissors, Copy, Bold, Code, Image as ImageIcon, Italic, Link, List, ListOrdered, Paperclip, Quote, Smile, Strikethrough, Underline, Video as VideoIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import type { Attachment } from '../api/types';
import { ImageAnnotationDialog, type AnnotationSource } from './ImageAnnotationDialog';
import { imageFromClick } from '../lib/imageFromClick';
import { Modal } from './Modal';
import { MediaViewer } from './MediaViewer';
import { type MediaSource } from '../lib/mediaReview';
import { commentAttachmentIds, readVideoComments, type VideoComment, type VideoCommentDraft, type VideoFrameDraft } from '../lib/videoComments';
import { persistVideoCommentGroup, serializeVideoCommentsForSave, singleFrameEdit } from '../lib/persistVideoComment';
import { referencedInlineAttachmentIds } from '../lib/richText';
import type { ReviewContext } from '../lib/reviewContext';
import '../styles/editor-focus.css';

const IMAGE_FILE_PATTERN = /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i;
const VIDEO_FILE_PATTERN = /\.(mp4|webm|mkv|avi|flv|mov|m4v|mxf)$/i;

// Native video controls cannot host a text caret. Give every video editable
// paragraphs on both sides; adjacent videos share their intervening paragraph.
// Split inline video/text drafts without replacing video nodes or their metadata.
function ensureVideoTextLines(root: HTMLDivElement, inserted?: HTMLVideoElement): HTMLElement | null {
  let caretLine: HTMLElement | null = null;
  const meaningful = (fragment: DocumentFragment) => Boolean(fragment.textContent?.trim() || fragment.querySelector('img,video,audio,hr'));
  const textLine = (node: Node | null): node is HTMLElement => node instanceof HTMLElement
    && node.matches('p,div,li,blockquote,pre') && !node.querySelector('img,video,audio,hr');
  const emptyLine = () => { const p = document.createElement('p'); p.append(document.createElement('br')); return p; };
  const videos = [...root.querySelectorAll('video')];
  const videoBlock = (video: HTMLVideoElement) => {
    let block: Node = video;
    while (block.parentNode && block.parentNode !== root) block = block.parentNode;
    return block;
  };
  for (const video of videos) {
    const block = videoBlock(video);
    if (block !== video) {
      const before = document.createRange(); before.selectNodeContents(block); before.setEndBefore(video);
      const after = document.createRange(); after.selectNodeContents(block); after.setStartAfter(video);
      const hasBefore = meaningful(before.cloneContents()), hasAfter = meaningful(after.cloneContents());
      // Extract the trailing fragment first so the leading range stays valid.
      if (hasAfter) {
        const trailing = block.cloneNode(false) as HTMLElement; trailing.removeAttribute('id');
        trailing.append(after.extractContents()); root.insertBefore(trailing, block.nextSibling);
      }
      if (hasBefore) {
        const leading = block.cloneNode(false) as HTMLElement; leading.removeAttribute('id');
        leading.append(before.extractContents()); root.insertBefore(leading, block);
      }
    }
  }
  for (const video of videos) {
    const block = videoBlock(video);
    if (!textLine(block.previousSibling)) root.insertBefore(emptyLine(), block);
    if (!textLine(block.nextSibling)) root.insertBefore(emptyLine(), block.nextSibling);
    if (video === inserted) caretLine = block.nextSibling as HTMLElement;
  }
  return caretLine;
}

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
  disabled?: boolean;
  onFiles?: (files: File[]) => void;
  onImageUpload?: (file: File) => Promise<Attachment | null>;
  onVideoUpload?: (file: File) => Promise<Attachment | null>;
  onImageReplace?: (id: number, file: File) => Promise<Attachment>;
  onAttachmentRemove?: (ids: number[]) => Promise<void>;
  variant?: 'default' | 'messenger';
  onSubmit?: () => void;
  canSubmit?: boolean;
  compact?: boolean;
  reviewContext?: ReviewContext;
}

export function RichTextEditor({ value, onChange, placeholder, minHeight = 120, disabled = false, onFiles, onImageUpload, onVideoUpload, onImageReplace, onAttachmentRemove, variant = 'default', onSubmit, canSubmit = true, compact = false, reviewContext }: RichTextEditorProps) {
  const editor = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [selectionMenu, setSelectionMenu] = useState(false);
  const [formatting, setFormatting] = useState(false);
  const [attachmentsMenu, setAttachmentsMenu] = useState(false);
  const [clipboardError,setClipboardError] = useState('');

  const [annotation, setAnnotation] = useState<AnnotationSource | null>(null);
  const [replacingImage, setReplacingImage] = useState(false);
  const [videoReview, setVideoReview] = useState<MediaSource | null>(null);
  const videos = useMemo(() => {
    const content = document.createElement('div'); content.innerHTML = DOMPurify.sanitize(value);
    return [...content.querySelectorAll<HTMLVideoElement>('video[data-attachment-id]')].map(video => ({ kind: 'video' as const, url: video.src, name: video.dataset.filename || 'Видео', attachmentId: Number(video.dataset.attachmentId), comments: readVideoComments(video.dataset.videoComments) }));
  }, [value]);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState('');
  const rangeRef = useRef<Range | null>(null);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (variant !== 'messenger') return;
    let keyboard = false;
    const update = () => {
      if (root.current) root.current.dataset.keyboardFocus = String(keyboard && root.current.contains(document.activeElement));
    };
    const keydown = (event: KeyboardEvent) => { if (event.key === 'Tab') keyboard = true; };
    const pointerdown = () => { keyboard = false; update(); };
    document.addEventListener('keydown', keydown, true);
    document.addEventListener('pointerdown', pointerdown, true);
    document.addEventListener('focusin', update, true);
    return () => {
      document.removeEventListener('keydown', keydown, true);
      document.removeEventListener('pointerdown', pointerdown, true);
      document.removeEventListener('focusin', update, true);
    };
  }, [variant]);
  const formatDock = useRef<HTMLDivElement>(null);
  const formatSelection = useRef(false);
  const [touchSelection, setTouchSelection] = useState(false);
  useEffect(() => {
    if (!compact) return;
    const changed = () => {
      const selection=window.getSelection();
      const within=!!editor.current?.contains(document.activeElement) && !!selection?.rangeCount && !selection.isCollapsed && !!editor.current?.contains(selection.anchorNode) && !!editor.current?.contains(selection.focusNode);
      if(within) rangeRef.current=selection.getRangeAt(0).cloneRange();
      setSelectionMenu(within);if(!within && !formatSelection.current)setFormatting(false);
    };
    const dismiss = () => { formatSelection.current=false; setFormatting(false); setAttachmentsMenu(false); setEmojiOpen(false); setSelectionMenu(false); };
    const close=(event:KeyboardEvent)=>{if(event.key==='Escape')dismiss();};
    const outside=(event:Event)=>{if(!root.current?.contains(event.target as Node) && !formatDock.current?.contains(event.target as Node) && !linkOpen)dismiss();};
    const hidden=()=>{if(document.hidden)dismiss();};
    document.addEventListener('selectionchange',changed);document.addEventListener('keydown',close);
    document.addEventListener('pointerdown',outside);document.addEventListener('focusin',outside);document.addEventListener('visibilitychange',hidden);
    window.addEventListener('blur',dismiss);
    return ()=>{document.removeEventListener('selectionchange',changed);document.removeEventListener('keydown',close);document.removeEventListener('pointerdown',outside);document.removeEventListener('focusin',outside);document.removeEventListener('visibilitychange',hidden);window.removeEventListener('blur',dismiss);};
  }, [compact, linkOpen]);
  const annotationTarget = useRef<HTMLImageElement | null>(null);
  const draftVideo = () => editor.current?.querySelector<HTMLVideoElement>(`video[data-attachment-id="${videoReview?.attachmentId}"]`);
  const updateVideoComments = (comments: VideoComment[]) => {
    const target = draftVideo();
    if (!target) throw new Error('Видео удалено из черновика.');
    target.dataset.videoComments = serializeVideoCommentsForSave([...comments].sort((a, b) => a.time - b.time || a.id.localeCompare(b.id)));
    onChange(editor.current?.innerHTML ?? '');
  };
  const saveVideoGroup = async (frames: VideoFrameDraft[], comment: VideoCommentDraft, previous?: VideoComment, voice?: File | null) => {
    const upload = onImageUpload;
    if (!upload || !draftVideo()) throw new Error('Видео удалено из черновика.');
    const { saved, obsoleteIds } = await persistVideoCommentGroup(frames, comment, previous, voice, {
      upload,
      rollback: async uploaded => { if (onAttachmentRemove) await onAttachmentRemove(uploaded.map(file => file.id)); },
      commit: saved => {
        if (!draftVideo()) throw new Error('Видео удалено из черновика.');
        const current = readVideoComments(draftVideo()?.dataset.videoComments);
        updateVideoComments([...current.filter(item => item.id !== saved.id), saved]);
      },
    });
    const referenced = new Set(referencedInlineAttachmentIds(editor.current?.innerHTML || ''));
    const unused = obsoleteIds.filter(id => !referenced.has(id));
    if (unused.length && onAttachmentRemove) await onAttachmentRemove(unused).catch(() => undefined);
    return saved;
  };
  const rememberCaret = () => {
    const selection = window.getSelection();
    if (selection?.rangeCount && editor.current?.contains(selection.anchorNode)) rangeRef.current = selection.getRangeAt(0).cloneRange();
  };
  const restoreCaret = () => {
    editor.current?.focus(); const range = rangeRef.current;
    if (range && editor.current?.contains(range.commonAncestorContainer)) { const selection = window.getSelection(); selection?.removeAllRanges(); selection?.addRange(range); }
  };

  const toggleTouchFormatting = () => {
    if (formatting) {
      formatSelection.current = false;
      restoreCaret(); window.getSelection()?.collapseToEnd();
      setFormatting(false); setSelectionMenu(false);
    } else {
      rememberCaret(); formatSelection.current = true;
      setFormatting(true); setAttachmentsMenu(false); setEmojiOpen(false);
      // Keep a cloned range for the command, but dismiss Android's selection
      // handles/action menu before showing our formatting controls.
      window.getSelection()?.removeAllRanges();
    }
  };

  useEffect(() => {
    if (editor.current && editor.current.innerHTML !== value) {
      editor.current.innerHTML = DOMPurify.sanitize(value);
      ensureVideoTextLines(editor.current);
    }
  }, [value]);

  const command = (name: string, argument?: string) => {
    if (disabled) return;
    restoreCaret();
    document.execCommand(name, false, argument);
    onChange(editor.current?.innerHTML ?? '');
    rememberCaret();
    if (compact && touchSelection) { formatSelection.current=false; window.getSelection()?.collapseToEnd(); setSelectionMenu(false); setFormatting(false); }
  };

  const insertAtCaret = (node: Node) => {
    const root = editor.current;
    if (!root) return;
    const selection = window.getSelection();
    if (selection?.rangeCount && root.contains(selection.anchorNode)) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(node);
      range.setStartAfter(node);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      root.append(node);
    }
  };

  const insertImage = (file: File): Promise<boolean> => {
    if (!onImageUpload) return Promise.resolve(false);
    const img = document.createElement('img');
    const blobUrl = URL.createObjectURL(file);
    img.src = blobUrl;
    img.setAttribute('data-pending', '');
    img.alt = file.name;
    insertAtCaret(img);
    onChange(editor.current?.innerHTML ?? '');
    return onImageUpload(file).then((attachment) => {
      const root = editor.current;
      if (root?.contains(img)) {
        if (attachment) {
          img.removeAttribute('data-pending');
          img.setAttribute('data-attachment-id', String(attachment.id));
          img.src = attachment.previewUrl || attachment.attachmentUrl;
          const link = document.createElement('a');
          link.href = attachment.attachmentUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          img.replaceWith(link);
          link.append(img);
        } else {
          img.remove();
        }
        onChange(root.innerHTML);
      }
      URL.revokeObjectURL(blobUrl);
      return Boolean(attachment);
    }, () => {
      img.remove();
      URL.revokeObjectURL(blobUrl);
      onChange(editor.current?.innerHTML ?? '');
      return false;
    });
  };

  const insertVideo = (file: File) => {
    if (!onVideoUpload) return;
    const video = document.createElement('video');
    const blobUrl = URL.createObjectURL(file);
    const pendingId = `video-${crypto.getRandomValues(new Uint32Array(2)).join('-')}`;
    video.dataset.uploadId = pendingId;
    const currentVideo = () => editor.current?.querySelector<HTMLVideoElement>(`video[data-upload-id="${pendingId}"]`);
    video.src = blobUrl;
    video.controls = true;
    video.dataset.filename = file.name;
    video.preload = 'metadata';
    video.setAttribute('data-pending', '');
    insertAtCaret(video);
    const root = editor.current;
    const line = root && ensureVideoTextLines(root, video);
    if (root && line) {
      root.focus();
      const range = document.createRange(); range.setStart(line, 0); range.collapse(true);
      const selection = window.getSelection(); selection?.removeAllRanges(); selection?.addRange(range);
      rememberCaret(); root.scrollTop = root.scrollHeight;
    }
    onChange(editor.current?.innerHTML ?? '');
    onVideoUpload(file).then((attachment) => {
      const root = editor.current;
      const target = currentVideo();
      if (root && target) {
        if (attachment) {
          target.removeAttribute('data-pending');
          target.removeAttribute('data-upload-id');
          target.setAttribute('data-attachment-id', String(attachment.id));
          target.dataset.videoReview = 'true';
          target.dataset.filename = attachment.name;
          target.src = attachment.attachmentUrl;
        } else {
          target.remove();
        }
        onChange(root.innerHTML);
      }
      URL.revokeObjectURL(blobUrl);
    }, () => {
      currentVideo()?.remove();
      URL.revokeObjectURL(blobUrl);
      onChange(editor.current?.innerHTML ?? '');
    });
  };

  const acceptFiles = (files: File[]) => {
    if (disabled) return false;
    if (!files.length || (!onFiles && !onImageUpload && !onVideoUpload)) return false;
    const images = onImageUpload
      ? files.filter((file) => file.type.startsWith('image/') || IMAGE_FILE_PATTERN.test(file.name))
      : [];
    const videos = onVideoUpload
      ? files.filter((file) => file.type.startsWith('video/') || VIDEO_FILE_PATTERN.test(file.name))
      : [];
    const others = files.filter((file) => !images.includes(file) && !videos.includes(file));
    if (!images.length && !videos.length && (!onFiles || !others.length)) return false;
    images.forEach(file => { void insertImage(file); });
    videos.forEach(insertVideo);
    if (others.length && onFiles) onFiles(others);
    return true;
  };

  return (
    <><div ref={root} onPointerDownCapture={e => { if (e.target instanceof Node && editor.current?.contains(e.target)) { setTouchSelection(e.pointerType === 'touch' || e.pointerType === 'pen'); formatSelection.current=false; setFormatting(false); } }} className={`rich-editor${variant === 'messenger' ? ' rich-editor--messenger' : ''}${compact ? ' rich-editor--compact' : ''}${dragging ? ' is-dragging' : ''}`}>
      {compact&&<>
        <button className="editor-emoji-trigger" disabled={disabled} aria-label="Эмодзи" aria-expanded={emojiOpen} onMouseDown={e=>{rememberCaret();e.preventDefault();}} onClick={()=>setEmojiOpen(v=>!v)}><Smile size={23}/></button>
        <button className="editor-attachment-trigger" disabled={disabled} aria-label="Вложения" aria-expanded={attachmentsMenu} onMouseDown={e=>{rememberCaret();e.preventDefault();}} onClick={()=>{setAttachmentsMenu(v=>!v);setFormatting(false);}}><Paperclip size={23}/></button>
        {selectionMenu&&!touchSelection&&<div className="editor-selection-menu" role="toolbar" aria-label="Действия с выделенным текстом" onMouseDown={e=>e.preventDefault()}>
          <button disabled={disabled} onClick={async()=>{restoreCaret();try{await navigator.clipboard.writeText(window.getSelection()?.toString()??'');restoreCaret();document.execCommand('delete');onChange(editor.current?.innerHTML??'');}catch{setClipboardError('Не удалось вырезать. Используйте системное меню или Ctrl+X.');}}}><Scissors size={16}/>Вырезать</button>
          <button onClick={async()=>{restoreCaret();try{await navigator.clipboard.writeText(window.getSelection()?.toString()??'');}catch{setClipboardError('Не удалось скопировать. Используйте системное меню или Ctrl+C.');}}}><Copy size={16}/>Копировать</button>
          <button aria-label="Форматировать выделенный текст" aria-expanded={formatting} onClick={()=>{setFormatting(v=>!v);setAttachmentsMenu(false);}}><EllipsisVertical size={18}/></button>
        </div>}
        {clipboardError&&<span className="editor-clipboard-error" role="alert">{clipboardError}</span>}
      </>}
      <fieldset disabled={disabled} onMouseDown={event => { if ((event.target as HTMLElement).closest('button')) { if (!formatSelection.current) rememberCaret(); event.preventDefault(); } }} className={`rich-editor__toolbar${compact ? (formatting ? ' is-formatting' : attachmentsMenu ? ' is-attachments' : ' is-hidden') : ''}`} role="toolbar" aria-label="Форматирование" style={{ margin: 0, border: 0, minWidth: 0 }}>
        <button aria-label="Жирный" onClick={() => command('bold')} type="button"><Bold size={16} /><span className="editor-tool-label">Жирный</span></button>
        <button aria-label="Курсив" onClick={() => command('italic')} type="button"><Italic size={16} /><span className="editor-tool-label">Курсив</span></button>
        <button aria-label="Подчёркнутый" onClick={() => command('underline')} type="button"><Underline size={16} /><span className="editor-tool-label">Подчеркнуть</span></button>
        <button aria-label="Зачёркнутый" onClick={() => command('strikeThrough')} type="button"><Strikethrough size={16} /><span className="editor-tool-label">Зачеркнуть</span></button>
        <button aria-label="Цитата" onClick={() => command('formatBlock', 'blockquote')} type="button"><Quote size={16} /><span className="editor-tool-label">Цитата</span></button>
        <button aria-label="Моноширинный текст" onClick={() => command('formatBlock', 'pre')} type="button"><Code size={16} /><span className="editor-tool-label">Моношрифт</span></button>
        <button aria-label="Добавить ссылку" onClick={() => { setLinkOpen(true); setLinkUrl(''); setLinkError(''); }} type="button"><Link size={16} /><span className="editor-tool-label">Ссылка</span></button>
        <button aria-label="Маркированный список" onClick={() => command('insertUnorderedList')} type="button"><List size={16} /><span className="editor-tool-label">Маркированный список</span></button>
        <button aria-label="Нумерованный список" onClick={() => command('insertOrderedList')} type="button"><ListOrdered size={16} /><span className="editor-tool-label">Нумерованный список</span></button>
        <button aria-label="Эмодзи" aria-expanded={emojiOpen} onClick={() => setEmojiOpen(!emojiOpen)} type="button"><Smile size={17}/><span className="editor-tool-label">Эмодзи</span></button>
        {onFiles || onImageUpload || onVideoUpload ? <span className="rich-editor__toolbar-spacer" /> : null}
        {onImageUpload ? (
          <>
            <input
              accept="image/*"
              hidden
              multiple
              onChange={(event) => {
                if (event.target.files) Array.from(event.target.files).forEach(file => { void insertImage(file); });
                event.target.value = ''; setAttachmentsMenu(false);
              }}
              ref={imageInput}
              type="file"
            />
            <button aria-label="Вставить изображение" onClick={() => imageInput.current?.click()} type="button"><ImageIcon size={16} /><span className="editor-tool-label">Фото</span></button>
          </>
        ) : null}
        {onVideoUpload ? (
          <>
            <input
              accept="video/*"
              hidden
              multiple
              onChange={(event) => {
                if (event.target.files) Array.from(event.target.files).forEach(insertVideo);
                event.target.value = ''; setAttachmentsMenu(false);
              }}
              ref={videoInput}
              type="file"
            />
            <button aria-label="Вставить видео" onClick={() => videoInput.current?.click()} type="button"><VideoIcon size={16} /><span className="editor-tool-label">Видео</span></button>
          </>
        ) : null}
        {onFiles ? (
          <>
            <input
              hidden
              multiple
              onChange={(event) => {
                if (event.target.files) acceptFiles(Array.from(event.target.files));
                event.target.value = ''; setAttachmentsMenu(false);
              }}
              ref={fileInput}
              type="file"
            />
            <button aria-label="Прикрепить файл" onClick={() => fileInput.current?.click()} type="button"><Paperclip size={16} /><span className="editor-tool-label">Файл</span></button>
          </>
        ) : null}
      </fieldset>
      {emojiOpen && <div className="editor-emoji-picker">{['🙂','👍','❤️','🔥','👌','🎬','👀','✅','🙏','🤔','💪','🎉'].map(emoji => <button key={emoji} type="button" disabled={disabled} onMouseDown={event => event.preventDefault()} onClick={() => { command('insertText', emoji); setEmojiOpen(false); }}>{emoji}</button>)}</div>}
      <div
        aria-label={placeholder ?? 'Текст'}
        className="rich-editor__content"
        contentEditable={!disabled}
        // Keep the message composer focused while sending temporarily locks editing.
        tabIndex={onSubmit ? 0 : undefined}
        aria-disabled={disabled}
        data-placeholder={placeholder}
        aria-multiline="true"
        onKeyUp={rememberCaret}
        onMouseUp={rememberCaret}
        onClick={event => {
          if (!disabled && event.target instanceof HTMLVideoElement && event.target.dataset.attachmentId) {
            const source = videos.find(item => item.attachmentId === Number((event.target as HTMLVideoElement).dataset.attachmentId));
            if (source) { event.preventDefault(); event.target.pause(); rememberCaret(); setVideoReview(source); }
            return;
          }
          if (disabled || !onImageUpload || !(event.target instanceof HTMLImageElement) || event.target.hasAttribute('data-pending')) return;
          const source = imageFromClick(event.target);
          if (source) { event.preventDefault(); rememberCaret(); annotationTarget.current = event.target; setReplacingImage(Boolean(event.target.dataset.attachmentId && onImageReplace)); setAnnotation(source); }
        }}
        onKeyDown={event => {
          if (onSubmit && (!compact || !window.matchMedia('(pointer: coarse)').matches || event.ctrlKey || event.metaKey) && event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); if (!disabled && canSubmit) onSubmit(); }
        }}
        onDragEnter={(event) => {
          if ((onFiles || onImageUpload || onVideoUpload) && event.dataTransfer.types.includes('Files')) setDragging(true);
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
        }}
        onDragOver={(event) => {
          if ((onFiles || onImageUpload || onVideoUpload) && event.dataTransfer.types.includes('Files')) event.preventDefault();
        }}
        onDrop={(event) => {
          setDragging(false);
          if (acceptFiles(Array.from(event.dataTransfer.files))) event.preventDefault();
        }}
        onInput={(event) => { ensureVideoTextLines(event.currentTarget); onChange(event.currentTarget.innerHTML); }}
        onPaste={(event) => {
          if (acceptFiles(Array.from(event.clipboardData.files))) { event.preventDefault(); return; }
          if (disabled) { event.preventDefault(); return; }
          const html = event.clipboardData.getData('text/html');
          if (html) { event.preventDefault(); document.execCommand('insertHTML', false, DOMPurify.sanitize(html, { ALLOWED_TAGS: ['p','br','div','b','strong','i','em','u','s','strike','blockquote','pre','code','a','ul','ol','li'], ALLOWED_ATTR: ['href'] })); onChange(editor.current?.innerHTML ?? ''); }
        }}
        ref={editor}
        role="textbox"
        style={{ minHeight }}
        suppressContentEditableWarning
      />
      {variant === 'messenger' && <div className="rich-editor__hint"><span>{onSubmit ? 'Enter — отправить · Shift + Enter — новая строка' : 'Вставьте или перетащите файл'}</span><span>Нажмите на картинку, чтобы нарисовать правки</span></div>}
      {videos.length > 0 && <div className="rich-editor__video-actions">{videos.map(video => <button key={video.attachmentId} type="button" disabled={disabled} className="video-comment-trigger" aria-label={`Комментарии к видео: ${video.name}`} title={`Комментировать видео: ${video.name}`} onClick={() => { editor.current?.querySelector<HTMLVideoElement>(`video[data-attachment-id="${video.attachmentId}"]`)?.pause(); rememberCaret(); setVideoReview(video); }}><MessageSquarePlus size={24}/>{video.comments.length > 0 && <span className="video-comment-trigger__count">{video.comments.length}</span>}</button>)}</div>}
      {videoReview && <MediaViewer source={{...videoReview,reviewContext}} onClose={() => setVideoReview(null)} videoDraft={onImageUpload ? {
        canSend: !disabled && canSubmit,
        send: onSubmit,
        save: (file, comment, previous, voice) => saveVideoGroup(singleFrameEdit(file, comment, previous), comment, previous, voice),
        saveGroup: saveVideoGroup,
        remove: async comment => {
          if (!onAttachmentRemove) throw new Error('Удаление вложений недоступно.');
          updateVideoComments(readVideoComments(draftVideo()?.dataset.videoComments).filter(item => item.id !== comment.id));
          const referenced = new Set(referencedInlineAttachmentIds(editor.current?.innerHTML || ''));
          await onAttachmentRemove(commentAttachmentIds(comment).filter(id => !referenced.has(id))).catch(() => undefined);
        },
      } : undefined}/>} 
      {annotation && <ImageAnnotationDialog source={annotation} replacing={replacingImage} onClose={() => setAnnotation(null)} onSave={async file => {
        const target = annotationTarget.current, id = Number(target?.dataset.attachmentId);
        if (target && id && onImageReplace) {
          const attachment = await onImageReplace(id, file);
          const current = editor.current?.querySelector<HTMLImageElement>(`img[data-attachment-id="${id}"]`);
          if (!current) throw new Error('Картинка удалена из черновика.');
          current.src = attachment.previewUrl || attachment.attachmentUrl; current.alt = attachment.name; current.dataset.attachmentId = String(attachment.id);
          const link = current.closest('a'); if (link) link.href = attachment.attachmentUrl;
          onChange(editor.current?.innerHTML ?? '');
        } else { restoreCaret(); if (!await insertImage(file)) throw new Error('Не удалось загрузить картинку с правками. Попробуйте ещё раз.'); }
      }}/>} 
      {linkOpen && <Modal title="Добавить ссылку" size="small" onClose={() => setLinkOpen(false)} actions={<><button className="button button--secondary" type="button" onClick={() => setLinkOpen(false)}>Отмена</button><button className="button button--primary" type="button" onClick={() => {
        try { const url = new URL(linkUrl); if (!['https:', 'http:', 'mailto:', 'tel:'].includes(url.protocol)) throw new Error(); restoreCaret(); if (window.getSelection()?.isCollapsed) document.execCommand('insertText', false, linkUrl); else document.execCommand('createLink', false, linkUrl); onChange(editor.current?.innerHTML ?? ''); setLinkOpen(false); }
        catch { setLinkError('Введите ссылку с https://, http://, mailto: или tel:'); }
      }}>Добавить</button></>}><label className="field"><span>Адрес</span><input autoFocus value={linkUrl} onChange={event => setLinkUrl(event.target.value)} placeholder="https://…"/></label>{linkError && <p className="form-error">{linkError}</p>}</Modal>}
    </div>{compact && touchSelection && (selectionMenu || formatting) && <div ref={formatDock} className="editor-format-dock">
      <button className="editor-format-action" type="button" disabled={disabled} aria-label={formatting ? 'Закрыть инструменты форматирования' : 'Форматировать выделенный текст'} aria-expanded={formatting} onPointerDown={event => event.preventDefault()} onMouseDown={event => event.preventDefault()} onClick={toggleTouchFormatting}>
        <Type size={22}/><span>{formatting ? 'Закрыть инструменты' : 'Форматировать'}</span>
      </button>
    </div>}</>
  );
}
